import Vuex from 'vuex'
import axios from 'axios'
import {get_local, get_session, set_local, set_session} from "./helpers";


// initialize Vuex store
const store = Vuex.createStore({
  state() {
    const locale = document.documentElement.getAttribute('lang') || "en-CA"
    return {
      cart: null,
      flyout: false,
      fetching: {},
      lang: locale.substring(0, 2),
      country: locale.substring(3, 5).toUpperCase(),
      all_stores: JSON.parse(get_session('all_stores')),
      location: JSON.parse(get_session('location')),
      product_availability: null,
      product_tile_availability: null,
      pids_to_query: {},
      favourite_store: parseInt(get_local('favourite_store')) || null,
      pending_review_badge_refreshes: 0,
      reviews_callback_mutated: false,
      fetching_carousels: 0,
      global_components: {},
      active_static_store: null,
      loaded_static_maps: [],
    }
  },
  getters: {
    closest_stores: (state) => {
      return state.all_stores ? {
        coords: state.all_stores.coords,
        stores: state.all_stores.stores.filter(e => !e.is_warehouse)
      } : null
    },
    currency: (state) => (value) => {
      if (String(value).includes("$")) {
        return value;
      } else {
        try {
          return new Intl.NumberFormat(state.lang, {
            style: 'currency',
            currency: 'CAD',
            currencyDisplay: 'narrowSymbol'
          }).format(value / 100);
        } catch (e) {
          return new Intl.NumberFormat(state.lang, {
            style: 'currency',
            currency: 'CAD',
            currencyDisplay: 'symbol'
          }).format(value / 100).replace("CA", "");
        }
      }
    },
    locale_uri_prefix: (state) => {
      return (state.lang === 'en' ? '' : '/fr')
    },
    locale_distance_string: (state) => (value) => {
      const num = Math.round((state.country === 'US' ? value * 0.621371 : value) * 10 / 10)
      return num.toString() + (state.country === 'US' ? ' mi' : ' km')
    },
    favorite_or_closest: (state, getters) => {
      if (state.favourite_store && getters.closest_stores && getters.store_dict[state.favourite_store]) {
        return getters.store_dict[state.favourite_store]
      }
      return (getters.closest_stores ? getters.closest_stores.stores[0] : null)
    },
    closest_store: (getters) => {
      return (getters.closest_stores ? getters.closest_stores.stores[0] : null)
    },
    store_dict: (state) => {
      if (!state.all_stores) {
        return null
      }
      let out = {}
      for (let store of state.all_stores.stores) {
        out[store.store_number] = store
      }
      return out
    },
    store_info: (state, getters) => (store_number) => {
      return (getters.store_dict !== null ? getters.store_dict[parseInt(store_number)] : null) || null
    },
    coords: (state) => {
      return `${state.location.latitude},${state.location.longitude}`
    },
    get_pickup_stores: (state, getters) => (availability_dict) => {
      const out = []
      for (let store of getters.closest_stores.stores) {
        if (availability_dict !== null && (availability_dict[store.store_number] || 0) === 0) {
          continue
        }
        let s = Object.assign({}, store)
        s.distance = (s.distance > 100 ? Math.round(s.distance) : Math.round(s.distance * 10) / 10)
        s.available = availability_dict !== null ? (availability_dict[s.store_number] || 0) > 0 : null
        out.push(s)
      }
      return out
    },
  },
  actions: {
    setFavouriteStore({getters, state, commit}, store_number) {
      commit({type: '_setFavouriteStore', value: store_number})
      dataLayer.push({
        'event': 'favourite_store',
        "fav_store_number": store_number,
        "fav_store_distance": store_number && getters.store_info(store_number) ?
            getters.store_info(store_number).distance : null
      })
    },
    getProductAvailability({commit, state, getters}, sfs_id) {
      axios.get(`/apps/api/stock/product?pid=${sfs_id}&coords=${getters.coords}&radius=-1`).then(resp => {
        commit({type: 'setProductAvailability', value: resp.data})
      }, error => {
        console.error("bad product stock response:", error.response)
      })
    },
    getCollectionOrProductsStock({commit, state, getters}, payload) {
      let cache_key = `${payload.type}-${payload.sfs_id}-${payload.pids}-${getters.coords}`
      let existing = get_session(cache_key)
      if (existing) {
        existing = JSON.parse(existing)
        if (new Date().getTime() - (existing.timestamp || 0) < 15 * 60 * 1000) {
          commit({type: 'setProductLaneStock', value: null})
          for (let output of existing.value) {
            commit({
              type: 'setProductLaneStock',
              value: output
            })
          }
          return
        }
      }

      let address = null
      if (payload.type === 'collection') {
        address = `/apps/api/stock/collection?collection_id=${payload.sfs_id}&coords=${getters.coords}&radius=-1`
      } else {
        address = `/apps/api/stock/products?pids=${payload.pids}&coords=${getters.coords}&radius=-1`
      }
      axios.get(address).then(
          response => {
            let store_dict = {}
            for (let store of Object.values(response.data.stores)) {
              store_dict[store.store_number] = {
                distance: store.distance,
                is_warehouse: store.is_warehouse,
                fulfillment: store.fulfillment
              }
            }
            commit({type: 'setProductLaneStock', value: null})
            const cache_output = []
            for (let pid of Object.keys(response.data.products)) {
              let product = response.data.products[pid]
              let output = {
                pid: pid,
                can_ship: false,
                pickup_25: false,
                pickup_50: false,
                pickup_100: false,
                pickup_250: false,
                pickup_any: false,
              }
              for (let store_stock of Object.values(product.variants)) {
                for (let store_num of Object.keys(store_stock)) {
                  let store = store_dict[parseInt(store_num)]
                  if (!output.can_ship && store.fulfillment && product.shippable) {
                    output.can_ship = true
                  }

                  if (!store.is_warehouse) {
                    if (!output.pickup_25 && store.distance <= 25.0) {
                      output.pickup_25 = true
                    }

                    if (!output.pickup_50 && store.distance <= 50.0) {
                      output.pickup_50 = true
                    }

                    if (!output.pickup_100 && store.distance <= 100.0) {
                      output.pickup_100 = true
                    }

                    if (!output.pickup_250 && store.distance <= 250.0) {
                      output.pickup_250 = true
                    }

                    if (!output.pickup_any) {
                      output.pickup_any = true
                    }
                  }
                }
                if (output.pickup_25 && output.can_ship) {
                  break
                }
              }
              cache_output.push(output)
              commit({
                type: 'setProductLaneStock',
                value: output
              })
            }
            set_session(cache_key, JSON.stringify({timestamp: new Date().getTime(), value: cache_output}))
          }, error => {
            console.error("bad collection/product stock response:", error.response)
            commit({type: 'setProductLaneStock', value: false})
          })
    },
    getCarouselItems({getters, commit, state, dispatch}, query) {
      query.coords = getters.coords
      commit("fetchingCarousels", {delta: 1})
      return new Promise((resolve, reject) => {
        axios.get(`/apps/api/carousel-products`, {params: query}).then(
            pid_resp => {
              const gq = `query ($shopify_ids: [ID!]!) @inContext(language: ${state.lang.toUpperCase()}) {
              nodes(ids: $shopify_ids) {... on Product {
                          id            
                          title            
                          tags            
                          handle            
                          productType
                          vendor
                          images(first:1) {edges{node{originalSrc}}}
                          badge: metafield(namespace: "badge", key: "locale-${state.lang}") {value}
                          source_exclusive: metafield(namespace: "details", key: "source-exclusive") {value}
                          review_stats: metafield(namespace: "reviews", key: "stats") {value}
                          variants(first: 1) {edges{node{
                            price{amount}
                            compareAtPrice{amount}
                          }}}}}}`

              const availability = pid_resp.data.products
              const pids = Object.keys(pid_resp.data.products)
              axios.post(`${storefront.url}/api/2023-07/graphql.json`,
                  {query: gq, variables: {shopify_ids: pids}},
                  {
                    headers: {
                      "X-Shopify-Storefront-Access-Token": storefront.token,
                      "Accept-Language": state.lang
                    }
                  }).then(
                  gql_resp => {
                    let current_index = window.impressions.length - 1
                    let products_out = []

                    if (gql_resp.data.data) {
                      for (let node of gql_resp.data.data.nodes) {
                        if (!node) {
                          continue
                        }
                        let prices = (node.variants.edges ? node.variants.edges[0].node : {})
                        let image = (node.images.edges ? {"url": node.images.edges[0]['node']['originalSrc']} : {})
                        let review_stats = node.review_stats ? JSON.parse(node.review_stats.value) : {
                          count: 0,
                          score: 0
                        }
                        products_out.push({
                          id: node.id.split("/").pop(),
                          price: parseFloat((prices.price || {}).amount) || null,
                          compare_at_price: parseFloat((prices.compareAtPrice || {}).amount) || null,
                          handle: node.handle || null,
                          featured_image: image,
                          title: node.title || null,
                          vendor: node.vendor || null,
                          product_type: node.productType || null,
                          badge: (node.badge && node.badge.value && node.badge.value !== '{}' ? JSON.parse(node.badge.value) : null),
                          source_exclusive: (node.source_exclusive ? node.source_exclusive.value === 'true' : null) || null,
                          tags: node.tags || [],
                          availability: availability[node.id],
                          review_count: review_stats.count,
                          review_score: review_stats.score
                        })
                      }
                    }

                    let products = products_out.map(e => {
                      current_index++
                      e.index = current_index
                      if (e.badge && e.badge.image) {
                        e.badge.image = window.fileUrlPrefix + e.badge.image
                      }
                      e.price_numeric = parseFloat(e.price)
                      if (e.price) {
                        e.price = getters.currency(e.price * 100)
                      }
                      e.compare_at_price_numeric = parseFloat(e.compare_at_price)
                      if (e.compare_at_price) {
                        e.compare_at_price = getters.currency(e.compare_at_price * 100)
                      }
                      if (e.featured_image && e.featured_image.url) {
                        e.featured_image.url += (e.featured_image.url.includes('?v=') ? '&' : '?') + 'width=208'
                      }
                      return e
                    })
                    commit("fetchingCarousels", {delta: -1, products: products, type_: query.type_})
                    if (state.fetching_carousels === 0) {
                      dispatch("fireImpressions")
                    }
                    resolve(products)
                  },
                  err => {
                    commit("fetchingCarousels", {delta: -1})
                    if (state.fetching_carousels === 0) {
                      dispatch("fireImpressions")
                    }
                    if (!(!err.response && err.code === "ECONNABORTED")) {
                      console.error("Error loading carousel storefront data:", err, err.response)
                    }
                    reject(new Error(`Error loading carousel storefront data: ${err.response} ${err}`))
                  })
            },
            err => {
              commit("fetchingCarousels", {delta: -1})
              if (state.fetching_carousels === 0) {
                dispatch("fireImpressions")
              }
              if (!(!err.response && err.code === "ECONNABORTED")) {
                console.error("Error loading carousel pids:", err, err.response)
              }
              reject(new Error(`Error loading carousel pids: ${err.response} ${err}`))
            }
        )
      })
    },
    fireImpressions({state}) {
      if (state.fetching_carousels > 0) {
        return
      }
      if (window.impressions && window.impressions.length > 0) {
        dataLayer.push({"ecommerce": null})
        dataLayer.push({
          "event": "view_item_list",
          "ecommerce": {
            "items": window.impressions
          }
        })
      }
    },
    getCart({commit, getters}) {
      return new Promise((resolve, reject) => {
        axios.get(getters.locale_uri_prefix + '/cart.js').then(response => {
          if (response.status === 200) {
            if (typeof response.data.item_count !== 'undefined') {
              commit({type: 'setCart', value: response.data})
              resolve()
            } else {
              console.error("bad cart response:", response, response.data)
              reject(new Error(`Bad cart response: ${response.data} ${response}`))
            }
          } else {
            console.error("non-200 cart response:", response, response.data)
            reject(new Error(`Non-200 cart response: ${response.data} ${response}`))
          }
        }, error => {
          if (error && error.response && error.response.status === 422) {
            return reject(new Error(`Cart - 422`))
          }
          if (!error.response && error.code === 'ECONNABORTED') {
            return reject(new Error(`Cart - Aborted`))
          }
          console.error("cart handler error:", error, error.request, error.response, error.message)
          if (error.response) {
            reject(new Error(`Cart bad response error: ${error.response} ${error}`))
          } else if (error.request) {
            reject(new Error(`Cart no-response error: ${error.request} ${error}`))
          } else {
            reject(new Error(`Cart pre-request error: ${error.response} ${error}`))
          }
        });
      })
    },
    getProductLaneStock({commit, state, dispatch}) {
      let to_fetch = []
      for (const [key, val] of Object.entries(state.pids_to_query)) {
        if (!val) {
          to_fetch.push(key)
        }
      }
      if (to_fetch.length > 0) {
        commit({type: "markPidsFetching", value: to_fetch})
        dispatch('getLocation').then(resp => {
          dispatch('getCollectionOrProductsStock', {type: "products", pids: to_fetch.join(",")}).then(() => {
          }, () => {
          })
        }, () => {
        })
      }
    },
    getAllStores({commit, state, getters}) {
      // Every component calls this, use recursive promise to ensure only the first component makes the network request
      function ensureNotFetching() {
        const start = Date.now();
        return new Promise(waitFor);

        function waitFor(res, rej) {
          if (!state.fetching.stores)
            res();
          else if ((Date.now() - start) >= 5000)
            res();
          else
            setTimeout(waitFor.bind(this, res, rej), 10);
        }
      }

      function make_request(res_req, rej_req) {
        commit({type: 'setFetching', value: ['stores', true]})
        if (getters.closest_stores !== null && `${state.location.latitude},${state.location.longitude}` === getters.closest_stores.coords) {
          commit({type: 'setFetching', value: ['stores', false]})
          res_req()
        } else {
          axios.get(`/apps/api/stores?coords=${state.location.latitude},${state.location.longitude}&radius=-1`).then(
              response => {
                if (response.data.length > 0) {
                  commit({type: 'setAllStores', value: response.data});
                  commit({type: 'setFetching', value: ['stores', false]})
                  res_req()
                } else {
                  console.error("bad stores response:", response.data)
                  commit({type: 'setFetching', value: ['stores', false]})
                  rej_req()
                }
              }, error => {
                console.error("bad stores response:", error.response)
                commit({type: 'setFetching', value: ['stores', false]})
                rej_req()
              })
        }
      }

      return new Promise((resolve, reject) => {
        if (state.fetching.stores) {
          ensureNotFetching().then(ready => {
            make_request(resolve, reject)
          }, () => {
          })
        } else {
          make_request(resolve, reject)
        }
      })
    },
    getLocation({commit, state}) {
      // Every component calls this, use recursive promise to ensure only the first component makes the network request
      function ensureNotFetching() {
        const start = Date.now();
        return new Promise(waitFor);

        function waitFor(res, rej) {
          if (!state.fetching.location)
            res();
          else if ((Date.now() - start) >= 5000)
            res();
          else
            setTimeout(waitFor.bind(this, res, rej), 10);
        }
      }

      function make_request(res_req, rej_req) {
        if (state.location !== null && state.location.latitude) {
          return res_req()
        }
        commit({type: 'setFetching', value: ['location', true]})
        axios.get('/apps/api/location').then(
            response => {
              if (response.status === 200) {
                commit({type: 'setLocation', value: response.data});
                commit({type: 'setFetching', value: ['location', false]})
                res_req()
              } else {
                console.error("bad location response:", response, response.data)
                commit({
                  type: 'setLocation', // hockey hall of fame (fallback)
                  value: {latitude: 43.6472761, longitude: -79.3798789, postal_code: "M5E 1X8", country: "CA"}
                });
                commit({type: 'setFetching', value: ['location', false]})
                rej_req(new Error(`Bad Location Response: ${response.data} ${response}`))
              }
            },
            error => {
              commit({type: 'setFetching', value: ['location', false]})
              console.error("error location response:", error, error.response)
              commit({
                type: 'setLocation', // hockey hall of fame (fallback)
                value: {latitude: 43.6472761, longitude: -79.3798789, postal_code: "M5E 1X8", country: "CA"}
              });
              rej_req(new Error(`Error Location Response: ${error.response} ${error}`))
            })
      }

      return new Promise((resolve, reject) => {
        if (state.fetching.location) {
          ensureNotFetching().then(ready => {
            make_request(resolve, reject)
          }, () => {
          })
        } else {
          make_request(resolve, reject)
        }
      })
    }
  },
  mutations: {
    addCartItem(state, item) {
      state.cart.items.push(item)
      state.cart.item_count += 1
      state.cart.total_price += item.final_price
      state.cart.original_total_price += item.original_price
    },
    setActiveStaticStore(state, store_number) {
      state.active_static_store = store_number
      if (store_number && !state.loaded_static_maps.includes(store_number)) {
        state.loaded_static_maps.push(store_number)
      }
    },
    registerComponent(state, payload) {
      state.global_components[payload.name] = payload.component
    },
    fetchingCarousels(state, payload) {
      state.fetching_carousels += payload.delta
      if (payload.products) {
        window.impressions = window.impressions.concat(payload.products.map(e => {
          return {
            "item_id": e.id,
            "item_name": e.title,
            "item_list_id": `carousel-${payload.type_}`,
            "item_list_name": `carousel-${payload.type_}`,
            "item_brand": e.vendor,
            "item_category": e.product_type,
            "source_exclusive": (e.source_exclusive ? "1" : null),
            "index": e.index,
            "price": e.price_numeric,
            "discount": e.compare_at_price_numeric && e.compare_at_price_numeric > e.price_numeric ? e.compare_at_price_numeric - e.price_numeric : 0,
            "currency": state.country === 'US' ? 'USD' : 'CAD'
          }
        }))
      }
    },
    setReviewCallbackMutated(state, payload) {
      state.reviews_callback_mutated = true
    },
    setPendingReviewRefreshes(state, payload) {
      state.pending_review_badge_refreshes += payload.value
    },
    setFetching(state, payload) {
      state.fetching[payload.value[0]] = payload.value[1]
    },
    _setFavouriteStore(state, payload) {
      state.favourite_store = parseInt(payload.value)
      set_local('favourite_store', payload.value)
    },
    addPidToQuery(state, payload) {
      if (payload.value in state.pids_to_query) {
        return
      }
      state.pids_to_query[payload.value] = false
    },
    markPidsFetching(state, payload) {
      for (let pid of payload.value) {
        state.pids_to_query[pid] = true
      }
    },
    setFlyout(state, payload) {
      state.flyout = payload.value;
    },
    setCart(state, payload) {
      state.cart = payload.value;
    },
    setAllStores(state, payload) {
      state.all_stores = {
        stores: payload.value,
        coords: `${state.location.latitude},${state.location.longitude}`
      }
      set_session('all_stores', JSON.stringify(state.all_stores));
    },
    setLocation(state, payload) {
      state.location = {
        latitude: payload.value.latitude || (state.location || {}).latitude,
        longitude: payload.value.longitude || (state.location || {}).longitude,
        postal_code: payload.value.postal_code || (state.location || {}).postal_code,
        country: payload.value.country || (state.location || {}).country
      };
      set_session('location', JSON.stringify(state.location));
    },
    setProductLaneStock(state, payload) {
      if (state.product_tile_availability === null) {
        state.product_tile_availability = {}
      }
      if (payload.value === false) {
        state.product_tile_availability = false
      } else if (payload.value) {
        state.product_tile_availability[parseInt(payload.value.pid)] = Object.freeze({
          can_ship: payload.value.can_ship,
          pickup_25: payload.value.pickup_25,
          pickup_50: payload.value.pickup_50,
          pickup_100: payload.value.pickup_100,
          pickup_250: payload.value.pickup_250,
          pickup_any: payload.value.pickup_any,
        })
      }
    },
    setProductAvailability(state, payload) {
      state.product_availability = payload.value
    }
  },
  devtools: false
});

export {store as default}